import { z } from 'zod';
import { platformJsonValueSchema } from '@crest/platform-contracts/settings';
import { platformImportResolutionCapability, platformImportResolutionProofSchema, } from '@crest/platform-contracts/import-resolution';
import { projectsImportResolutionCapability, projectsImportResolutionSchema, } from './projects-import-resolution.contract.js';
/** Metadata-only import of the legacy project file tree. Object transfer is a later gate. */
export const projectsProjectFilesImportCapability = 'projects.project-files-import.v1';
export const projectsProjectFilesImportContractVersion = projectsProjectFilesImportCapability;
export const projectsProjectFilesImportManifestVersion = 'projects-project-files-source-manifest.v1';
export const projectsProjectFilesImportMappingVersion = 'projects-project-files-normalized-v2';
export const projectsProjectFolderPathMappingVersion = 'projects-project-folder-path.v1';
export const projectsProjectFilesImportFolderEntityType = 'project-folder';
export const projectsProjectFilesImportFileEntityType = 'project-file';
export const projectsProjectFilesImportEntityTypeSchema = z.enum([
    projectsProjectFilesImportFolderEntityType,
    projectsProjectFilesImportFileEntityType,
]);
const timestamp = z.string().datetime({ offset: true });
const sourceId = z.string().trim().min(1).max(500);
const tenantKey = z.string().trim().min(1).max(240);
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);
const uuid = z.string().uuid();
const safeName = z.string().trim().min(1).max(240).superRefine((value, context) => {
    if (value === '.' || value === '..' || /[\\/\u0000-\u001f\u007f]/.test(value)) {
        context.addIssue({ code: 'custom', message: 'File or folder name is not safe.' });
    }
});
const safePath = z.string().trim().min(1).max(2_000).superRefine((value, context) => {
    if (/[\u0000-\u001f\u007f]/.test(value) || value.startsWith('/') || value.includes('..')) {
        context.addIssue({ code: 'custom', message: 'File folder path is not safe.' });
    }
});
const legacySourcePath = z.string().min(1).max(2_000);
export const projectsProjectFilesImportWriterFenceSchema = z.strictObject({
    fenceEpoch: z.string().trim().min(1).max(240),
    sourceWriterState: z.enum(['authoritative', 'frozen']),
    targetWriterState: z.literal('disabled'),
    noDualWriters: z.literal(true),
    sourceThrough: timestamp,
    attestationSha256: sha256,
    attestedAt: timestamp,
}).superRefine((value, context) => {
    if (Date.parse(value.attestedAt) < Date.parse(value.sourceThrough)) {
        context.addIssue({ code: 'custom', path: ['attestedAt'], message: 'Writer attestation cannot predate sourceThrough.' });
    }
});
export const projectsProjectFilesImportSourceManifestSchema = z.strictObject({
    manifestVersion: z.literal(projectsProjectFilesImportManifestVersion),
    sourceSystem: z.literal('crest-crm'),
    sourceRepository: z.string().trim().min(1).max(240),
    sourceSystemRevision: z.string().trim().min(1).max(240),
    sourceKind: z.enum(['synthetic', 'sanitized', 'restricted']),
    sourceCommitSha: z.string().regex(/^[a-f0-9]{40}$/),
    sourceTreeSha: z.string().regex(/^[a-f0-9]{40}$/),
    sourceSchemaSha256: sha256,
    captureMode: z.enum(['base', 'final-delta']),
    capturedAt: timestamp,
    writerFence: projectsProjectFilesImportWriterFenceSchema,
    tables: z.array(z.union([
        z.strictObject({ tableName: z.literal('file_folders'), rowCount: z.number().int().nonnegative().safe(), sha256 }),
        z.strictObject({ tableName: z.literal('files'), rowCount: z.number().int().nonnegative().safe(), sha256 }),
    ])).length(2),
}).superRefine((value, context) => {
    if (Date.parse(value.capturedAt) !== Date.parse(value.writerFence.sourceThrough)) {
        context.addIssue({ code: 'custom', path: ['writerFence', 'sourceThrough'], message: 'Writer watermark must equal capture time.' });
    }
    if (value.captureMode === 'final-delta' && value.writerFence.sourceWriterState !== 'frozen') {
        context.addIssue({ code: 'custom', path: ['writerFence', 'sourceWriterState'], message: 'Final delta requires a frozen source writer.' });
    }
    if (value.tables[0]?.tableName !== 'file_folders' || value.tables[1]?.tableName !== 'files') {
        context.addIssue({ code: 'custom', path: ['tables'], message: 'Manifest tables must be ordered file_folders then files.' });
    }
});
/** The extractor adds sourceId/sourceObjectKey/metadataOnly; this contract keeps only safe metadata. */
export const projectsProjectFilesImportFolderSourceSchema = z.strictObject({
    id: sourceId,
    project_id: sourceId.nullable(),
    contact_id: sourceId.nullable(),
    parent_id: sourceId.nullable(),
    name: safeName,
    /** Preserved verbatim as evidence. It is never used as the target folder path. */
    path: legacySourcePath,
    is_system: z.boolean(),
    created_by: sourceId.nullable(),
    created_at: timestamp,
});
export const projectsProjectFilesImportFileSourceSchema = z.strictObject({
    id: sourceId,
    project_id: sourceId.nullable(),
    contact_id: sourceId.nullable(),
    storage_path: z.string().trim().min(1).max(2_000).nullable(),
    storage_bucket: z.string().trim().min(1).max(240).nullable(),
    file_name: safeName,
    file_type: z.string().trim().min(1).max(255).nullable(),
    file_size_bytes: z.number().int().positive().max(Number.MAX_SAFE_INTEGER).nullable(),
    category: z.string().trim().min(1).max(80).nullable(),
    taken_at: timestamp.nullable(),
    uploaded_by: sourceId.nullable(),
    created_at: timestamp.nullable(),
    folder_id: sourceId.nullable(),
    gps_lat: z.number().min(-90).max(90).nullable(),
    gps_lng: z.number().min(-180).max(180).nullable(),
    gps_accuracy_m: z.number().nonnegative().max(100_000).nullable(),
    room_label: z.string().trim().min(1).max(120).nullable(),
    photo_phase: z.string().trim().min(1).max(80).nullable(),
    notes: z.string().max(20_000).nullable(),
    /** Added by the extractor; no URL, bytes, or object content is carried. */
    sourceObjectKey: z.string().trim().min(1).max(2_000).nullable(),
    metadataOnly: z.literal(true),
    /** Optional only for a future object-transfer manifest; never inferred. */
    sourceContentSha256: sha256.nullable().optional(),
    sourceByteCount: z.number().int().positive().max(Number.MAX_SAFE_INTEGER).nullable().optional(),
});
export const projectsProjectFilesImportRowSchema = z.discriminatedUnion('sourceTable', [
    z.strictObject({ sequence: z.number().int().positive().safe(), sourceTable: z.literal('file_folders'), sourceTenantExternalKey: tenantKey, sourceId, sourceSha256: sha256, sourceUpdatedAt: timestamp.nullable(), payload: projectsProjectFilesImportFolderSourceSchema }),
    z.strictObject({ sequence: z.number().int().positive().safe(), sourceTable: z.literal('files'), sourceTenantExternalKey: tenantKey, sourceId, sourceSha256: sha256, sourceUpdatedAt: timestamp.nullable(), payload: projectsProjectFilesImportFileSourceSchema }),
]);
export const projectsProjectFilesImportSnapshotPayloadSchema = z.strictObject({
    contractVersion: z.literal(projectsProjectFilesImportCapability),
    mappingVersion: z.literal(projectsProjectFilesImportMappingVersion),
    sourceSystem: z.literal('crest-crm'),
    tenantExternalKey: tenantKey,
    runKind: z.enum(['base', 'final-delta']),
    sourceEpoch: z.string().trim().min(1).max(240),
    parentSourceSha256: sha256.nullable(),
    sourceManifest: projectsProjectFilesImportSourceManifestSchema,
    membershipSourceMappings: z.array(z.strictObject({ sourceTenantExternalKey: tenantKey, sourceUserId: sourceId, membershipSourceExternalKey: sourceId })).max(100_000).optional(),
    rows: z.array(z.unknown()).max(100_000),
}).superRefine((value, context) => {
    if ((value.runKind === 'base') !== (value.parentSourceSha256 === null))
        context.addIssue({ code: 'custom', path: ['parentSourceSha256'], message: 'Base has no parent; final delta requires an exact parent.' });
    if (value.runKind !== value.sourceManifest.captureMode)
        context.addIssue({ code: 'custom', path: ['sourceManifest', 'captureMode'], message: 'Manifest mode must match run kind.' });
    const folderCount = value.rows.filter((row) => typeof row === 'object' && row !== null && row.sourceTable === 'file_folders').length;
    const fileCount = value.rows.filter((row) => typeof row === 'object' && row !== null && row.sourceTable === 'files').length;
    if (value.sourceManifest.tables[0]?.rowCount !== folderCount || value.sourceManifest.tables[1]?.rowCount !== fileCount || value.rows.length !== folderCount + fileCount)
        context.addIssue({ code: 'custom', path: ['sourceManifest', 'tables'], message: 'Manifest table counts must exactly reconcile to snapshot rows.' });
});
export const projectsProjectFilesImportSnapshotSchema = projectsProjectFilesImportSnapshotPayloadSchema.extend({ sourceManifestSha256: sha256, sourceSha256: sha256 });
export const projectsProjectFilesImportProjectResolutionSchema = z.strictObject({ sourceExternalKey: sourceId, sourceTenantExternalKey: tenantKey, resolution: projectsImportResolutionSchema, verified: z.literal(true) }).superRefine((value, context) => {
    if (value.sourceExternalKey !== value.resolution.sourceId || value.resolution.capability !== projectsImportResolutionCapability || value.resolution.entityType !== 'project')
        context.addIssue({ code: 'custom', path: ['resolution'], message: 'Project resolution must bind the supplied source key.' });
});
export const projectsProjectFilesImportMembershipResolutionSchema = z.strictObject({ sourceExternalKey: sourceId, sourceUserId: sourceId, sourceTenantExternalKey: tenantKey, resolution: platformImportResolutionProofSchema, verified: z.literal(true) }).superRefine((value, context) => {
    if (value.sourceExternalKey !== value.resolution.sourceId || value.resolution.capability !== platformImportResolutionCapability || value.resolution.entityType !== 'membership')
        context.addIssue({ code: 'custom', path: ['resolution'], message: 'Membership resolution must bind the supplied source key.' });
    if (value.sourceTenantExternalKey !== value.resolution.sourceTenantExternalKey)
        context.addIssue({ code: 'custom', path: ['sourceTenantExternalKey'], message: 'Membership resolution must be for the supplied source tenant.' });
});
const sourceObjectIdentitySchema = z.strictObject({ sourceFileId: sourceId, sourceBucket: z.string().trim().min(1).max(240), sourceObjectKey: z.string().trim().min(1).max(2_000), sourceContentSha256: sha256, sourceByteCount: z.number().int().positive().max(Number.MAX_SAFE_INTEGER) });
export const projectsProjectFilesImportFolderTargetPayloadSchema = z.strictObject({ targetKind: z.literal('folder'), projectId: uuid, parentFolderId: uuid.nullable(), name: safeName, path: safePath, isSystem: z.boolean(), createdAt: timestamp, createdByMembershipId: uuid });
export const projectsProjectFilesImportFileTargetPayloadSchema = z.strictObject({ targetKind: z.literal('file'), projectId: uuid, folderId: uuid.nullable(), originalFileName: safeName, category: z.enum(['photo', 'video', 'document', 'other']), contentType: z.string().trim().min(1).max(255), sizeBytes: z.number().int().positive().max(Number.MAX_SAFE_INTEGER), sha256, /** A verified/read-back handoff is attached and therefore cannot remain pending. */ state: z.literal('attached'), roomLabel: z.string().trim().min(1).max(120).nullable(), phase: z.enum(['before', 'during', 'after', 'other']).nullable(), capturedAt: timestamp.nullable(), capturedLatitude: z.number().min(-90).max(90).nullable(), capturedLongitude: z.number().min(-180).max(180).nullable(), capturedAccuracyMeters: z.number().nonnegative().max(100_000).nullable(), uploadedByMembershipId: uuid, /** Current project_files requires these IDs; only a verified Storage handoff can satisfy them. */ objectTransfer: z.strictObject({ status: z.literal('verified'), uploadIntentId: uuid, platformObjectId: uuid, receiptId: uuid, receiptSha256: sha256 }) });
export const projectsProjectFilesImportTargetPayloadSchema = z.discriminatedUnion('targetKind', [
    projectsProjectFilesImportFolderTargetPayloadSchema,
    projectsProjectFilesImportFileTargetPayloadSchema,
]);
export const projectsProjectFilesImportSourceObjectIdentitySchema = sourceObjectIdentitySchema;
export const projectsProjectFolderPathMappingSchema = z.strictObject({
    mappingVersion: z.literal(projectsProjectFolderPathMappingVersion),
    strategy: z.literal('hierarchy-name-source-id'),
    sourcePathPreserved: z.literal(true),
    parentSourceId: sourceId.nullable(),
    targetPath: safePath,
});
export const projectsProjectFolderCreatorProvenanceSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('legacy-created-by'), sourceUserId: sourceId }),
    z.strictObject({
        kind: z.literal('import-actor-fallback'),
        reason: z.literal('legacy-created-by-null'),
        importActorSourceUserId: sourceId,
    }),
]);
export const projectsProjectFileUploaderProvenanceSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('legacy-uploaded-by'), sourceUserId: sourceId }),
    z.strictObject({
        kind: z.literal('import-actor-fallback'),
        reason: z.literal('legacy-uploaded-by-null'),
        importActorSourceUserId: sourceId,
    }),
]);
export const projectsProjectFilesImportRejectReasonSchema = z.enum([
    'schema-invalid', 'cross-tenant', 'duplicate-source-identity', 'source-timestamp-mismatch', 'source-after-capture', 'invalid-owner', 'contact-owned', 'unresolved-project', 'unresolved-membership', 'unresolved-created-by-membership', 'unresolved-uploader-membership', 'missing-parent-folder', 'cycle-detected', 'missing-required-value', 'unsupported-content-type', 'object-transfer-pending',
]);
export const projectsProjectFilesImportRejectSchema = z.strictObject({ sequence: z.number().int().positive().safe(), entityType: projectsProjectFilesImportEntityTypeSchema, sourceId, sourceSha256: sha256, reasonCode: projectsProjectFilesImportRejectReasonSchema, fieldPaths: z.array(z.string().trim().min(1).max(240)).max(20) });
const folderActionBase = {
    sequence: z.number().int().positive().safe(),
    entityType: z.literal(projectsProjectFilesImportFolderEntityType),
    sourceId,
    sourceSha256: sha256,
    sourceUpdatedAt: timestamp.nullable(),
    targetId: uuid,
    identityStrategy: z.literal('deterministic-v5'),
    payload: projectsProjectFilesImportFolderTargetPayloadSchema,
    sourceEvidence: projectsProjectFilesImportFolderSourceSchema,
    projectResolution: projectsProjectFilesImportProjectResolutionSchema,
    folderPathMapping: projectsProjectFolderPathMappingSchema,
};
const legacyCreatorFolderActionSchema = z.strictObject({
    ...folderActionBase,
    creatorProvenance: z.strictObject({ kind: z.literal('legacy-created-by'), sourceUserId: sourceId }),
    createdByMembershipResolution: projectsProjectFilesImportMembershipResolutionSchema,
}).superRefine((value, context) => {
    if (value.sourceEvidence.created_by === null
        || value.sourceEvidence.created_by !== value.creatorProvenance.sourceUserId
        || value.sourceEvidence.created_by !== value.createdByMembershipResolution.sourceUserId) {
        context.addIssue({ code: 'custom', path: ['createdByMembershipResolution'], message: 'Legacy creator evidence must bind the exact non-null source creator.' });
    }
});
const importActorFolderActionSchema = z.strictObject({
    ...folderActionBase,
    creatorProvenance: z.strictObject({
        kind: z.literal('import-actor-fallback'),
        reason: z.literal('legacy-created-by-null'),
        importActorSourceUserId: sourceId,
    }),
    importActorMembershipResolution: projectsProjectFilesImportMembershipResolutionSchema,
}).superRefine((value, context) => {
    if (value.sourceEvidence.created_by !== null
        || value.creatorProvenance.importActorSourceUserId !== value.importActorMembershipResolution.sourceUserId) {
        context.addIssue({ code: 'custom', path: ['importActorMembershipResolution'], message: 'Import actor fallback is allowed only for an explicitly null legacy creator.' });
    }
});
const fileActionBase = {
    sequence: z.number().int().positive().safe(),
    entityType: z.literal(projectsProjectFilesImportFileEntityType),
    sourceId,
    sourceSha256: sha256,
    sourceUpdatedAt: timestamp.nullable(),
    targetId: uuid,
    identityStrategy: z.literal('deterministic-v5'),
    payload: projectsProjectFilesImportFileTargetPayloadSchema,
    sourceEvidence: projectsProjectFilesImportFileSourceSchema,
    sourceObjectIdentity: sourceObjectIdentitySchema,
    projectResolution: projectsProjectFilesImportProjectResolutionSchema,
};
const legacyUploaderFileActionSchema = z.strictObject({
    ...fileActionBase,
    uploaderProvenance: z.strictObject({ kind: z.literal('legacy-uploaded-by'), sourceUserId: sourceId }),
    uploaderMembershipResolution: projectsProjectFilesImportMembershipResolutionSchema,
}).superRefine((value, context) => {
    if (value.sourceEvidence.uploaded_by === null
        || value.sourceEvidence.uploaded_by !== value.uploaderProvenance.sourceUserId
        || value.sourceEvidence.uploaded_by !== value.uploaderMembershipResolution.sourceUserId) {
        context.addIssue({ code: 'custom', path: ['uploaderMembershipResolution'], message: 'Legacy uploader evidence must bind the exact non-null source uploader.' });
    }
});
const importActorUploaderFileActionSchema = z.strictObject({
    ...fileActionBase,
    uploaderProvenance: z.strictObject({
        kind: z.literal('import-actor-fallback'),
        reason: z.literal('legacy-uploaded-by-null'),
        importActorSourceUserId: sourceId,
    }),
    importActorMembershipResolution: projectsProjectFilesImportMembershipResolutionSchema,
}).superRefine((value, context) => {
    if (value.sourceEvidence.uploaded_by !== null
        || value.uploaderProvenance.importActorSourceUserId !== value.importActorMembershipResolution.sourceUserId) {
        context.addIssue({ code: 'custom', path: ['importActorMembershipResolution'], message: 'Import actor fallback is allowed only for an explicitly null legacy uploader.' });
    }
});
export const projectsProjectFilesImportPlanActionSchema = z.union([
    legacyCreatorFolderActionSchema,
    importActorFolderActionSchema,
    legacyUploaderFileActionSchema,
    importActorUploaderFileActionSchema,
]);
export const projectsProjectFilesImportPlanSchema = z.strictObject({ contractVersion: z.literal(projectsProjectFilesImportCapability), manifestVersion: z.literal(projectsProjectFilesImportManifestVersion), mappingVersion: z.literal(projectsProjectFilesImportMappingVersion), sourceSystem: z.literal('crest-crm'), sourceRepository: z.string().trim().min(1).max(240), sourceSystemRevision: z.string().trim().min(1).max(240), sourceKind: z.enum(['synthetic', 'sanitized', 'restricted']), sourceCommitSha: z.string().regex(/^[a-f0-9]{40}$/), sourceTreeSha: z.string().regex(/^[a-f0-9]{40}$/), sourceSchemaSha256: sha256, sourceManifestSha256: sha256, tenantExternalKey: tenantKey, runKind: z.enum(['base', 'final-delta']), sourceEpoch: z.string().trim().min(1).max(240), parentSourceSha256: sha256.nullable(), capturedAt: timestamp, writerFence: projectsProjectFilesImportWriterFenceSchema, sourceSha256: sha256, planSha256: sha256, actions: z.array(projectsProjectFilesImportPlanActionSchema), rejects: z.array(projectsProjectFilesImportRejectSchema), counts: z.strictObject({ sourceRows: z.number().int().nonnegative(), plannedRows: z.number().int().nonnegative(), rejectedRows: z.number().int().nonnegative() }) }).superRefine((value, context) => { if (value.counts.sourceRows !== value.actions.length + value.rejects.length || value.counts.plannedRows !== value.actions.length || value.counts.rejectedRows !== value.rejects.length)
    context.addIssue({ code: 'custom', path: ['counts'], message: 'Every source row produces exactly one action or reject.' }); });
export const projectsProjectFilesImportExecutionReceiptSchema = z.strictObject({ sequence: z.number().int().positive().safe(), entityType: projectsProjectFilesImportEntityTypeSchema, sourceId, sourceSha256: sha256, sourceUpdatedAt: timestamp.nullable(), targetId: uuid, identityStrategy: z.literal('deterministic-v5'), outcome: z.enum(['applied', 'skipped', 'verified']), previousTargetSha256: sha256.nullable(), targetSha256: sha256, targetCanonical: projectsProjectFilesImportTargetPayloadSchema, rollbackToken: platformJsonValueSchema });
export const projectsProjectFilesImportRollbackReceiptSchema = z.strictObject({ sequence: z.number().int().positive().safe(), outcome: z.enum(['applied', 'already-rolled-back', 'rejected-conflict', 'rejected-state']), detailCode: z.string().trim().min(3).max(120), targetSha256: sha256.nullable() });
//# sourceMappingURL=projects-project-files-import.contract.js.map